import React from 'react';
import * as yup from 'yup';
import { SparkForm } from 'sharedComponents/SparkForm';
import { JoinedSparks } from 'types/Spark/JoinedSpark';
import SparkBasicDetails from './SparkBasicDetails';
import SparkPrivacyDetails from './SparkPrivacyDetails';
import SparkCategoryDetails from './SparkCategoryDetails';
import SettingsFooter from '../SparkSettingsFooter';

import MainInfoFormSkeleton from 'sharedComponents/Skeleton/MainInfoFormSkeleton';

export type MainInfoFormType = {
  name: string;
  headline: string;
  supportUrl: string;
  marketingUrl: string;
  copyrightUrl: string;
  shortDescription: string;
  privacyPolicyUrl: string;
  detailedDescription: string;
  sparkPrivacyTypeID: string;
  price: number;
  billingTypeID: string | '';
  trialUses: number;
  subCategories: string[];
  mainCategoryID: string;
};

type MainInfoFormProps = {
  joinedSpark: JoinedSparks | undefined;
  isLoading?: boolean;
  onSave: (values: MainInfoFormType) => Promise<void>;
  setSelectedItemIndex: React.Dispatch<React.SetStateAction<number>>;
};

function MainInfoForm({
  joinedSpark,
  setSelectedItemIndex,
  onSave,
  isLoading,
}: MainInfoFormProps) {
  const sparkCategories = joinedSpark?.sparkCategories;
  const sparkAttributes = joinedSpark?.attributes;

  const subCategoryIDs = sparkCategories
    ?.filter((category) => !category.attributes.mainCategory)
    .map((category) => category.attributes.categoryID);

  const mainCategoryID = sparkCategories?.find(
    (category) => category.attributes.mainCategory
  )?.attributes.categoryID;

  const validationSchema = React.useMemo(() => {
    return yup.object({
      name: yup
        .string()
        .required('Should be between 10 and 50 characters')
        .min(3, 'Name must be at least 3 characters')
        .max(50),
      headline: yup
        .string()
        .required('Should be between 10 and 50 characters')
        .min(10, 'Headline must be at least 10 characters')
        .max(50),
      shortDescription: yup
        .string()
        .required('Should be between 50 and 150 characters')
        .min(50, 'Description must be at least 50 characters')
        .max(150),
      detailedDescription: yup
        .string()
        .required('Should be between 50 and 1000 characters')
        .min(50, 'Description must be at least 50 characters')
        .max(1000),
      sparkPrivacyTypeID: yup.string().required('Privacy option is Required'),
      price: yup.string(),
      trialUses: yup.string(),
      billingTypeID: yup.string().when('price', {
        is: (price: string) => parseFloat(price) > 0,
        then: (schema) => schema.required('Please select'),
        otherwise: (schema) => schema.notRequired(),
      }),
      supportUrl: yup
        .string()
        .url('Support URL must be a valid URL')
        .when('sparkPrivacyTypeID', {
          is: (value: string) => value === '3',
          then: (schema) => schema.required('Support URL is required'),
          otherwise: (schema) => schema.notRequired(),
        }),
      marketingUrl: yup
        .string()
        .url('Marketing URL must be a valid URL')
        .when('sparkPrivacyTypeID', {
          is: (value: string) => value === '3',
          then: (schema) => schema.required('Marketing URL is required'),
          otherwise: (schema) => schema.notRequired(),
        }),
      privacyPolicyUrl: yup
        .string()
        .url('Privacy Policy URL must be a valid URL')
        .when('sparkPrivacyTypeID', {
          is: (value: string) => value === '3',
          then: (schema) => schema.required('Privacy Policy URL is required'),
          otherwise: (schema) => schema.notRequired(),
        }),
      copyrightUrl: yup
        .string()
        .url('Copyright URL must be a valid URL')
        .when('sparkPrivacyTypeID', {
          is: (value: string) => value === '3',
          then: (schema) => schema.required('Copyright URL is required'),
          otherwise: (schema) => schema.notRequired(),
        }),
      mainCategoryID: yup.string().required('Main category is Required'),
      subCategories: yup
        .array()
        .of(yup.string().required())
        .required('SubCategories are required')
        .min(1, 'Please add at least one subcategory to proceed.'),
    });
  }, []);

  const initialValues: MainInfoFormType = {
    name: sparkAttributes?.nameforHuman || '',
    headline: sparkAttributes?.headline || '',
    shortDescription: sparkAttributes?.descriptionforHuman || '',
    copyrightUrl: sparkAttributes?.copyright || '',
    detailedDescription: sparkAttributes?.overview || '',
    marketingUrl: sparkAttributes?.marketingURL || '',
    privacyPolicyUrl: sparkAttributes?.privacyPolicyURL || '',
    supportUrl: sparkAttributes?.supportURL || '',
    sparkPrivacyTypeID: sparkAttributes?.sparkPrivacyTypeID
      ? sparkAttributes?.sparkPrivacyTypeID + ''
      : '',
    mainCategoryID: mainCategoryID || '',
    subCategories: subCategoryIDs || [],
    price: sparkAttributes?.price || 0,
    billingTypeID: sparkAttributes?.sparkBillingTypeID
      ? sparkAttributes?.sparkBillingTypeID + ''
      : '',
    trialUses: sparkAttributes?.trialUses || 0,
  };

  const handleSubmit = (values: MainInfoFormType) => {
    setSelectedItemIndex(1);
    onSave(values);
  };

  if (isLoading) {
    return (
      <>
        <MainInfoFormSkeleton />
      </>
    );
  }

  return (
    <SparkForm<MainInfoFormType>
      initialValues={initialValues}
      onSave={handleSubmit}
      validationSchema={validationSchema}
      containerProps={{
        paddingRight: '10px',
        position: 'absolute',
      }}
    >
      <SparkBasicDetails />
      <SparkPrivacyDetails />
      <SparkCategoryDetails />
      <SettingsFooter primaryButtonText='Next Step: Images and Media' />
    </SparkForm>
  );
}

export default MainInfoForm;
